import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useIsMobile from '@/features/shared/Hooks/useIsMobile';
import { Swiper } from 'swiper';
import { isUndefined } from 'lodash';
import { datalayerPush } from '@/features/shared/HOCs/withGTM';
import TagManager from 'react-gtm-module';
import { useTheme } from '@material-ui/core/styles';

const CARD_STACKED_COUNT = 4;

export const getIndexTitle = ({ maxIndex = 0, currentIndex = 0, isMobile = false }) => {
  let diff = 0;
  if (maxIndex === currentIndex && isMobile) diff = -maxIndex;
  if (maxIndex === currentIndex && !isMobile) diff = -maxIndex - 1;

  return currentIndex + diff + (isMobile ? 0 : 1);
};

export const useContentCardCarousel = (
  content: ContentListType<CardType>,
  mobileStacked?: boolean,
) => {
  const isMobile = useIsMobile();
  const { contentList: contentListRaw = [], cta } = content;

  const contentList = useMemo(() => {
    if (isMobile) return contentListRaw;
    if (contentListRaw.length < 5) return contentListRaw;

    const contentListCloned = [...contentListRaw];
    const lastNode = contentListCloned.pop();
    return [lastNode, ...contentListCloned];
  }, [contentListRaw, isMobile]) as CardType[];

  const useSwiper = useMemo(() => {
    if (isMobile) return !mobileStacked && contentList.length > 1;
    return contentList.length > CARD_STACKED_COUNT;
  }, [contentList.length, isMobile, mobileStacked]);

  return { contentList, useSwiper, cta };
};

export const useContentCarouselModel = (
  contentList: CardType[],
  useSwiper: boolean,
  classes: any,
) => {
  const [slideIndex, setSlideIndex] = useState<number>();
  const [triggerCarouselNavClick, setTriggerCarouselNavClick] = React.useState(false);
  const isFirstLoadRef = React.useRef(true);
  const rootRef = useRef<HTMLDivElement>(null);
  const [slider, setSlider] = useState<any>(null);
  const triggerCardContentClick = useRef(false);
  const muiTheme = useTheme();
  const isMobile = useIsMobile();

  React.useLayoutEffect(() => {
    if (!isFirstLoadRef.current) return;

    setTimeout(() => {
      isFirstLoadRef.current = false;
    });
  }, []);

  const configuration = useMemo(
    () => ({
      spaceBetween: 32,
      slidesPerView: 1,
      pagination: { clickable: true, el: `.${classes.sliderNavigation}` },
      loop: isMobile,
      loopedSlides: isMobile ? contentList.length : null,
      slideToClickedSlide: true,
      preventInteractionOnTransition: true,
      allowTouchMove: true,
      centerSlides: true,
      speed: 300,
      onSwiper: (swiper: Swiper) => {
        setSlider(swiper);
      },
      onSlideChangeTransitionEnd: ({ realIndex }: Swiper) => {
        if (isFirstLoadRef.current) return;
        setTriggerCarouselNavClick(false);
        setSlideIndex(realIndex);

        Promise.resolve().then(() => {
          setTriggerCarouselNavClick(() => true);
        });
        setTimeout(() => {
          triggerCardContentClick.current = false;
        });
      },
      breakpoints: {
        [muiTheme.breakpoints.width('md')]: {
          speed: 0,
          spaceBetween: 30,
          virtualTranslate: true,
          simulateTouch: false,
        },
      },
    }),
    [classes.sliderNavigation, contentList.length, isMobile, muiTheme.breakpoints],
  );

  useEffect(() => {
    if (isUndefined(slideIndex) || !rootRef?.current) {
      return;
    }

    const variantName = (rootRef.current.closest('[data-datalayer-variant]') as HTMLDivElement)
      ?.dataset?.datalayerVariant;
    datalayerPush({
      compRef: rootRef,
      args: {
        slideIndex,
        variantName,
      },
      event: 'gtm:custom:scroll',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideIndex]);

  const getIndex = (idx: number): number => {
    return idx + (slider?.realIndex || 0);
  };

  const setIndex = useCallback(
    (idx: number) => {
      if (slider && !slider.destroyed) {
        const lastIndex = contentList.length - 1;
        const index = getIndex(idx) - 1;
        const nextIndex = index < 0 ? lastIndex : index;
        slider?.slideTo(nextIndex % contentList.length);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [slider],
  );

  React.useEffect(() => {
    if (!triggerCarouselNavClick) return;
    if (!useSwiper) return;

    const currentIndex = slideIndex || 0;
    const maxIndex = contentList?.length - 1;
    const indexTitle = getIndexTitle({ maxIndex, currentIndex, isMobile });
    const isNavigatedFromContentCardClick = triggerCardContentClick.current;

    const dataLayer = {
      event: isNavigatedFromContentCardClick
        ? 'trackEvent-cardCarouselCardClick'
        : 'trackEvent-cardCarouselNavClick',
      event_name: isNavigatedFromContentCardClick
        ? 'card_carousel_card_click'
        : 'card_carousel_nav_click',
      title: contentList?.[indexTitle]?.cardHeading,
      index_num: String(currentIndex),
    };
    TagManager.dataLayer({ dataLayer });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerCarouselNavClick]);

  return { rootRef, slider, configuration, getIndex, triggerCardContentClick, setIndex };
};
